<template>
  <div>
    <q-dialog v-model="passagesDialog">
      <q-card style="width: 700px; max-width: 90vw">
        <q-toolbar>
          <q-toolbar-title class="text-blue-brand text-bold"
            >Passages</q-toolbar-title
          >
          <span class="text-caption text-grey-7 q-mr-xs">Marker Name:</span
          ><span class="text-caption text-grey-9">{{
            selectedMarkerName
          }}</span>
          <q-space />
          <q-btn
            v-if="sortedMarkers.length > 1"
            flat
            round
            dense
            icon="chevron_left"
            color="blue-brand"
            :disable="loading"
            @click.stop="onPrevMarker"
          >
            <q-tooltip>Previous marker</q-tooltip>
          </q-btn>
          <q-btn
            v-if="sortedMarkers.length > 1"
            flat
            round
            dense
            icon="chevron_right"
            color="blue-brand"
            :disable="loading"
            @click.stop="onNextMarker"
          >
            <q-tooltip>Next marker</q-tooltip>
          </q-btn>
          <q-btn
            flat
            round
            dense
            icon="refresh"
            :loading="loading"
            @click="fetchPassages"
          />
          <q-btn
            flat
            round
            dense
            icon="filter_alt"
            :color="filterGreenMl ? 'green' : 'grey-6'"
            @click="filterGreenMl = !filterGreenMl"
          >
            <q-tooltip>{{
              filterGreenMl
                ? 'Showing green ML results only'
                : 'Filter to green ML results'
            }}</q-tooltip>
          </q-btn>
          <q-btn
            v-close-popup
            flat
            round
            dense
            icon="close"
            @click="closeViewPsgs"
          />
        </q-toolbar>

        <div v-if="errorMsg" class="q-pb-sm">
          <q-badge class="q-ml-sm" color="red">
            {{ errorMsg }}
          </q-badge>
        </div>

        <q-table
          v-model:pagination="passagePagination"
          table-header-class="bg-blue-brand text-white"
          :rows="displayedPassages"
          :columns="passageColumns"
          row-key="passageId"
          dense
          :loading="loading"
          no-data-label="No passages received"
        >
          <template #body-cell-graph-data="props">
            <q-td :props="props">
              <template v-if="isManualPassage(props.row)">
                <q-badge color="grey" class="q-mr-xs">
                  {{ getWaveformLabel('H') }}
                </q-badge>
              </template>
              <span v-else-if="(props.row.passageWaveforms || []).length === 1">
                <q-btn
                  v-for="wf in props.row.passageWaveforms"
                  :key="wf.passageWaveformId"
                  size="sm"
                  class="q-mr-xs btn-brand"
                  dense
                  icon="show_chart"
                  :label="getWaveformLabel(wf.passageWaveformType)"
                  @click="onShowChart(props.row, wf)"
                ></q-btn>
              </span>
              <span v-else-if="(props.row.passageWaveforms || []).length > 1">
                <q-btn-dropdown
                  size="sm"
                  class="q-mr-xs btn-brand"
                  dense
                  icon="show_chart"
                >
                  <q-list dense>
                    <q-item
                      v-for="wf in props.row.passageWaveforms"
                      :key="wf.passageWaveformId"
                      v-close-popup
                      clickable
                      @click="onShowChart(props.row, wf)"
                    >
                      <q-item-section>
                        <q-item-label>{{
                          getWaveformLabel(wf.passageWaveformType)
                        }}</q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-btn-dropdown>
              </span>
              <span v-if="!props.row.passageId">[manual passage] </span>
              <q-btn
                v-if="
                  !props.row.passageId &&
                  (getuser() == 'admin' || getuser() == 'superadmin')
                "
                size="sm"
                class="btn-brand"
                dense
                color="blue-brand"
                icon="edit"
                @click="onConfirmEditManualPassage(props.row)"
              ></q-btn>
            </q-td>
          </template>
          <template #body-cell-uid="props">
            <q-td :props="props">{{ props.value }}</q-td>
          </template>
          <template #body-cell-verify-data="props">
            <q-td :props="props">
              <q-btn
                v-if="props.row.passageId"
                size="sm"
                class="btn-brand"
                dense
                :color="isAnyWaveformVerified(props.row) ? 'green' : 'red'"
                :icon="
                  isAnyWaveformVerified(props.row)
                    ? 'check_circle_outline'
                    : 'block'
                "
                @click="onVerifyPassage(props.row)"
              >
              </q-btn>

              <q-btn
                v-if="
                  !props.row.passageId &&
                  (getuser() == 'admin' || getuser() == 'superadmin')
                "
                size="sm"
                class="btn-brand"
                dense
                color="red"
                icon="delete"
                @click="onConfirmDeleteManualPassage(props.row)"
              >
              </q-btn>
            </q-td>
          </template>

          <template #body-cell-machine-learning-data="props">
            <q-td :props="props">
              <MLBadge
                v-for="wf in getMLWaveforms(props.row)"
                :key="wf.passageWaveformId"
                :type="wf.passageWaveformType"
                :prediction="wf.mlPrediction"
                :confidence="wf.mlConfidence"
                :results="wf.machineLearningResults"
              />
            </q-td>
          </template>
        </q-table>
      </q-card>
    </q-dialog>

    <WaveformVerifyDialog
      ref="waveformVerifyDialog"
      @confirm="onWaveformVerifyConfirm"
      @cancel="onWaveformVerifyCancel"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { EventBus } from '@/utils/EventBus';
import { IPassage } from '@/models/Passage';
import {
  PigRunMapEntry,
  PipelineSectionMarkerState,
} from '@/models/PigRunState';
import {
  WaveformTypeLabel,
  IPassageWaveformSummary,
} from '@/models/PassageWaveform';
import {
  isAnyWaveformVerified,
  getAvailableWaveforms,
} from '@/utils/WaveformUtils';
import PigRunService from '@/services/PigRunService';
import { PassageVerificationRequest } from '@/models/ReportManager/PassageVerificationRequest';
import { validatePassageVerification } from '@/utils/PassageValidation';
import WaveformVerifyDialog from '@/components/WaveformVerifyDialog.vue';
import MLBadge from '@/components/MLBadge.vue';

export default defineComponent({
  name: 'PipelineSectionMarkerPassagesDialog',
  components: { WaveformVerifyDialog, MLBadge },
  data() {
    return {
      passagesDialog: false,
      loading: false,
      selectedMarkerName: '',
      activePipelineSectionMarkerId: null as number | null,
      activePigRunId: null as number | null,
      passages: [] as IPassage[],
      errorMsg: '',
      passageColumns: [
        {
          name: 'passageTimeStamp',
          label: 'Time (UTC)',
          field: 'passageTimeStamp',
          align: 'left' as const,
          sortable: true,
        },
        {
          name: 'uid',
          label: 'UID',
          field: 'uid',
          align: 'left' as const,
          sortable: true,
        },
        {
          name: 'detectionCount',
          label: 'Det Count',
          field: 'detectionCount',
          align: 'left' as const,
          sortable: true,
        },
        {
          name: 'graph-data',
          label: 'Waveforms',
          field: 'graph-data',
          align: 'left' as const,
        },
        {
          name: 'verify-data',
          label: 'Verify',
          field: 'verify-data',
          align: 'center' as const,
        },
        {
          name: 'machine-learning-data',
          label: 'ML',
          field: 'machine-learning-data',
          align: 'center' as const,
        },
      ],
      passagePagination: { rowsPerPage: 10 },
      currentMarkerIndex: -1,
      sortedMarkers: [] as PipelineSectionMarkerState[],
      pendingVerifyPassage: null as IPassage | null,
      unsubscribeAction: null as (() => void) | null,
      refreshDebounceTimer: null as ReturnType<typeof setTimeout> | null,
      refreshMaxWaitTimer: null as ReturnType<typeof setTimeout> | null,
      filterGreenMl: false,
    };
  },
  computed: {
    displayedPassages(): IPassage[] {
      if (!this.filterGreenMl) return this.passages;
      return this.passages.filter((row) =>
        this.getMLWaveforms(row).some((wf) =>
          this.wfColor(wf).includes('green')
        )
      );
    },
  },
  created() {
    this.unsubscribeAction = this.$store.subscribeAction((action: any) => {
      if (action.type === 'passageRecieved') {
        this.onSignalRPassageOrRunUpdate();
      }
      if (
        action.type === 'refreshSinglePigRun' &&
        this.activePigRunId != null &&
        action.payload === this.activePigRunId
      ) {
        this.onSignalRPassageOrRunUpdate();
      }
      if (
        action.type === 'mlResultReceived' &&
        this.activePigRunId != null &&
        action.payload?.pigRunIds?.includes(this.activePigRunId)
      ) {
        this.onSignalRPassageOrRunUpdate();
      }
    });
  },
  mounted() {
    EventBus.on('switch-passage', this.handleSwitchPassage as any);
    EventBus.on('passage-verified', this.handlePassageVerified as any);
  },
  beforeUnmount() {
    EventBus.off('switch-passage', this.handleSwitchPassage as any);
    EventBus.off('passage-verified', this.handlePassageVerified as any);
    if (this.unsubscribeAction) {
      this.unsubscribeAction();
    }
    if (this.refreshDebounceTimer) {
      clearTimeout(this.refreshDebounceTimer);
    }
    if (this.refreshMaxWaitTimer) {
      clearTimeout(this.refreshMaxWaitTimer);
    }
  },
  methods: {
    async openNew(
      pipelineSectionMarkerId: number,
      markerName: string,
      pigRunId?: number
    ) {
      this.activePipelineSectionMarkerId = pipelineSectionMarkerId;
      this.selectedMarkerName = markerName;
      this.activePigRunId = pigRunId ?? null;
      this.sortedMarkers = [];
      this.currentMarkerIndex = -1;
      this.passages = [];
      this.errorMsg = '';
      this.passagesDialog = true;
      if (pigRunId) {
        await this.loadMarkers(pigRunId, pipelineSectionMarkerId);
      }
      await this.fetchPassages();
    },
    async loadMarkers(pigRunId: number, currentMarkerId: number) {
      // Try the active pig runs in the store first
      const entry = (
        this.$store.state.pigRun.pigRuns as Record<number, PigRunMapEntry>
      )[pigRunId];
      if (entry) {
        this.sortedMarkers = [...entry.markers].sort(
          (a, b) => a.sortOrder - b.sortOrder
        );
      } else {
        // Completed run — fetch markers from API
        try {
          const response = await PigRunService.getPigRun(pigRunId);
          const markers = response.data.markers || [];
          this.sortedMarkers = [...markers].sort(
            (a: any, b: any) => a.sortOrder - b.sortOrder
          );
        } catch (error) {
          console.error('Failed to load markers for pig run:', error);
        }
      }
      this.currentMarkerIndex = this.sortedMarkers.findIndex(
        (m: any) => m.pipelineSectionMarkerId === currentMarkerId
      );
    },
    async navigateToMarker(marker: PipelineSectionMarkerState) {
      this.activePipelineSectionMarkerId = marker.pipelineSectionMarkerId;
      this.selectedMarkerName = marker.name;
      this.currentMarkerIndex = this.sortedMarkers.findIndex(
        (m) => m.pipelineSectionMarkerId === marker.pipelineSectionMarkerId
      );
      this.passages = [];
      this.errorMsg = '';
      await this.fetchPassages();
    },
    onPrevMarker() {
      const markers = this.sortedMarkers;
      if (markers.length < 2 || this.currentMarkerIndex < 0) return;
      const prevIndex =
        (this.currentMarkerIndex - 1 + markers.length) % markers.length;
      this.navigateToMarker(markers[prevIndex]);
    },
    onNextMarker() {
      const markers = this.sortedMarkers;
      if (markers.length < 2 || this.currentMarkerIndex < 0) return;
      const nextIndex = (this.currentMarkerIndex + 1) % markers.length;
      this.navigateToMarker(markers[nextIndex]);
    },
    async fetchPassages() {
      if (!this.activePipelineSectionMarkerId) return;
      this.loading = true;
      try {
        const response = await PigRunService.getMarkerPassages(
          this.activePipelineSectionMarkerId
        );
        this.passages = response.data;
      } catch (error) {
        console.error('Failed to fetch marker passages:', error);
        this.errorMsg = 'Failed to load passages';
      } finally {
        this.loading = false;
      }
    },
    closeViewPsgs() {
      this.passagesDialog = false;
    },
    getuser(): string {
      return this.$store.state.user?.role || '';
    },
    isManualPassage(row: IPassage): boolean {
      const wfs = row.passageWaveforms || [];
      return wfs.length === 1 && wfs[0].passageWaveformType === 'H';
    },
    getWaveformLabel(type: string): string {
      return WaveformTypeLabel[type] || type;
    },
    isAnyWaveformVerified(passage: IPassage): boolean {
      return isAnyWaveformVerified(passage);
    },
    onShowChart(row: IPassage, wf: IPassageWaveformSummary) {
      this.$store.dispatch('setPassageDialogSource', 'marker_passages');

      const passageCopy = {
        ...row,
        requestedWaveformId: wf.passageWaveformId,
        pipelineSectionMarkerId: this.activePipelineSectionMarkerId,
        pigRunId: this.activePigRunId,
      };
      this.$store.dispatch('addPassageIdsToUpdate', [wf.passageWaveformId]);
      this.$store.dispatch('passageChartShow', passageCopy);
    },
    onConfirmEditManualPassage(_row: any) {},
    async onVerifyPassage(passage: IPassage) {
      if (!(this.getuser() === 'admin' || this.getuser() === 'superadmin'))
        return;

      const waveforms = getAvailableWaveforms(passage);
      if (waveforms.length === 0) return;

      // If any waveform is verified, unverify all
      if (isAnyWaveformVerified(passage)) {
        this.$store.dispatch('unverifyAllPassageWaveforms', { passage });
        EventBus.emit('passage-verified', { passage });
        return;
      }

      // Check verified passage and timestamp ordering
      try {
        const check = await validatePassageVerification(
          this.activePipelineSectionMarkerId!,
          this.activePigRunId,
          passage.passageTimeStamp
        );
        if (!check.valid) {
          this.$q.notify({
            type: check.warning ? 'warning' : 'negative',
            timeout: check.warning ? 0 : undefined,
            actions: check.warning
              ? [{ icon: 'close', color: 'white', round: true }]
              : undefined,
            message: (check.warning ?? check.error)!,
          });
          return;
        }
      } catch (error) {
        console.error('Passage verification validation failed:', error);
        return;
      }

      // Single waveform: verify directly
      if (waveforms.length === 1) {
        (passage as any).markerName = this.selectedMarkerName;
        const passageWaveformId = waveforms[0].value;
        if (!passage.uid) {
          // Manual passages have no uid — call API directly
          await this.verifyManualPassage(passage, passageWaveformId);
        } else {
          this.$store.dispatch('verifyPassage', {
            passage,
            passageWaveformId,
          });
          EventBus.emit('passage-verified', { passage });
        }
        return;
      }

      // Multiple waveforms: show preview selection dialog
      this.pendingVerifyPassage = passage;
      const firstWfId = waveforms[0].value;
      (this.$refs.waveformVerifyDialog as any).open(passage.uid!, firstWfId);
    },
    onWaveformVerifyConfirm(passageWaveformId: number) {
      if (this.pendingVerifyPassage) {
        (this.pendingVerifyPassage as any).markerName = this.selectedMarkerName;
        this.$store.dispatch('verifyPassage', {
          passage: this.pendingVerifyPassage,
          passageWaveformId,
        });
        EventBus.emit('passage-verified', {
          passage: this.pendingVerifyPassage,
        });
      }
      this.pendingVerifyPassage = null;
    },
    onWaveformVerifyCancel() {
      this.pendingVerifyPassage = null;
    },
    async verifyManualPassage(passage: IPassage, passageWaveformId: number) {
      const wf = (passage.passageWaveforms || []).find(
        (w) => w.passageWaveformId === passageWaveformId
      );
      if (!wf) return;
      const newVerified = !wf.isVerified;
      try {
        await PigRunService.setPassageWaveformVerification(
          new PassageVerificationRequest(passageWaveformId, newVerified)
        );
        wf.isVerified = newVerified;
        EventBus.emit('passage-verified', { passage });
      } catch (error) {
        console.error('Failed to verify manual passage:', error);
        this.$q.notify({
          type: 'negative',
          message: 'Unable to verify passage. Please try again.',
        });
      }
    },
    onConfirmDeleteManualPassage(_row: any) {},
    onSignalRPassageOrRunUpdate() {
      if (!this.passagesDialog || !this.activePipelineSectionMarkerId) return;
      // Trailing-edge debounce: reset the short timer on every message so
      // we don't fetch on every single event during a burst.
      if (this.refreshDebounceTimer) {
        clearTimeout(this.refreshDebounceTimer);
      }
      this.refreshDebounceTimer = setTimeout(() => {
        this.flushRefresh();
      }, 1500);
      // Max-wait guarantee: if messages keep arriving without a gap,
      // force a fetch after 5 seconds so the dialog never goes stale.
      if (!this.refreshMaxWaitTimer) {
        this.refreshMaxWaitTimer = setTimeout(() => {
          this.flushRefresh();
        }, 5000);
      }
    },
    flushRefresh() {
      if (this.refreshDebounceTimer) {
        clearTimeout(this.refreshDebounceTimer);
        this.refreshDebounceTimer = null;
      }
      if (this.refreshMaxWaitTimer) {
        clearTimeout(this.refreshMaxWaitTimer);
        this.refreshMaxWaitTimer = null;
      }
      this.fetchPassages();
    },
    handleSwitchPassage({
      passage,
      direction,
      type,
    }: {
      passage: any;
      direction: 'next' | 'previous';
      type: string;
    }) {
      if (!this.passagesDialog) return;
      const currentWaveformId =
        passage.requestedWaveformId || passage.passageId;

      const currentIndex = this.passages.findIndex((psg: any) =>
        (psg.passageWaveforms || []).some(
          (wf: any) => wf.passageWaveformId === currentWaveformId
        )
      );

      if (currentIndex === -1) return;

      const inc = direction === 'next' ? 1 : -1;
      const nextIndex =
        (currentIndex + inc + this.passages.length) % this.passages.length;
      const nextRow = this.passages[nextIndex];
      const nextWf =
        (nextRow.passageWaveforms || []).find(
          (wf: any) => wf.passageWaveformType === type
        ) || (nextRow.passageWaveforms || [])[0];
      if (nextWf) {
        this.onShowChart(nextRow, nextWf);
      }
    },
    handlePassageVerified({ passage }: any) {
      const idx = this.passages.findIndex(
        (p: any) => p.passageId === passage.passageId
      );
      if (idx !== -1) {
        this.passages[idx] = {
          ...this.passages[idx],
          passageWaveforms: [...passage.passageWaveforms],
        };
      }
    },
    getMLWaveforms(row: IPassage): IPassageWaveformSummary[] {
      return (row.passageWaveforms || []).filter(
        (wf) => wf.mlPrediction != null && wf.mlConfidence != null
      );
    },
    getMLMean(prediction: boolean, confidence: number): number {
      return (2 * Number(prediction) - 1) * confidence;
    },
    getMLColor(prediction: boolean, confidence: number): string {
      const mean = this.getMLMean(prediction, confidence);
      if (mean > 0.89) return 'green';
      if (mean > 0.75) return 'green-4';
      if (mean > -0.25) return 'orange';
      if (mean > -0.5) return 'red-3';
      return 'red';
    },
    getMLIcon(color: string): string {
      if (color.includes('red')) return 'block';
      if (color.includes('green')) return 'check_circle_outline';
      return 'help';
    },
    getMLConfidenceLabel(prediction: boolean, confidence: number): string {
      const mean = this.getMLMean(prediction, confidence);
      return ' ' + (50 * mean + 50).toFixed(1) + '%';
    },
    wfColor(wf: IPassageWaveformSummary): string {
      return this.getMLColor(wf.mlPrediction ?? false, wf.mlConfidence ?? 0);
    },
    wfLabel(wf: IPassageWaveformSummary): string {
      const type =
        WaveformTypeLabel[wf.passageWaveformType] || wf.passageWaveformType;
      const mean = this.getMLMean(
        wf.mlPrediction ?? false,
        wf.mlConfidence ?? 0
      );
      const pct = Math.round(50 * mean + 50) + '%';
      return `${type} ${pct}`;
    },
  },
});
</script>
